// AdminRequests.js - সম্পূর্ণ updated version with cancel functionality
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const AdminRequests = ({ setMessage }) => {
    const { getAdminRequests, approveAdminRequest, apiRequest } = useAdmin();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(null); // Track which request is being processed

    useEffect(() => {
        console.log('🔄 AdminRequests component mounted');
        loadRequests();
    }, []);

    const loadRequests = async () => {
        try {
            setLoading(true);
            console.log('🔄 Loading admin requests...');

            const requestsData = await getAdminRequests();
            console.log('✅ Loaded requests:', requestsData);

            setRequests(requestsData);

            if (requestsData.length === 0) {
                console.log('ℹ️ No pending requests found - checking database...');

                // Database check করার জন্য temporary log
                const token = localStorage.getItem('userToken');
                const directResponse = await fetch('https://travel-agency-server-lilac.vercel.app/api/auth/admin-requests', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const directData = await directResponse.json();
                console.log('🔍 Direct API call result:', directData);
            }
        } catch (error) {
            console.error('❌ Error loading admin requests:', error);
            setMessage('Error loading admin requests: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (userId, userName) => {
        try {
            setProcessing(userId);
            console.log(`🔄 Approving admin request for user: ${userName} (${userId})`);

            await approveAdminRequest(userId);
            setMessage(`✅ Admin request for ${userName} approved successfully!`);
            await loadRequests();
        } catch (error) {
            console.error('❌ Error approving request:', error);
            setMessage('Error approving request: ' + error.message);
        } finally {
            setProcessing(null);
        }
    };

    const handleCancel = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to cancel the admin request for ${userName}? This will reject their admin access request.`)) {
            return;
        }

        try {
            setProcessing(userId);
            console.log(`🔄 Cancelling admin request for user: ${userName} (${userId})`);

            // ✅ NEW: Cancel admin request API call
            const data = await apiRequest(`/auth/cancel-admin-request/${userId}`, {
                method: 'POST'
            });

            if (data.success) {
                setMessage(`❌ Admin request for ${userName} has been cancelled.`);
                await loadRequests();
            } else {
                throw new Error(data.error || 'Failed to cancel admin request');
            }
        } catch (error) {
            console.error('❌ Error cancelling request:', error);
            setMessage('Error cancelling request: ' + error.message);
        } finally {
            setProcessing(null);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading admin requests...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1 fw-bold">Admin Requests</h2>
                    <p className="text-muted mb-0">Approve or cancel admin access requests from users</p>
                </div>
                {requests.length > 0 && (
                    <div className="badge bg-warning text-dark fs-6">
                        <i className="fas fa-clock me-1"></i>
                        Pending: {requests.length}
                    </div>
                )}
            </div>

            {requests.length === 0 ? (
                <div className="text-center py-5">
                    <div className="mb-4">
                        <i className="fas fa-inbox fa-3x text-muted"></i>
                    </div>
                    <h4 className="text-muted">No Pending Requests</h4>
                    <p className="text-muted">
                        There are no pending admin requests at the moment.
                        <br />
                        <small>Users need to request admin access from their profile.</small>
                    </p>
                    <button
                        onClick={loadRequests}
                        className="btn btn-outline-primary btn-sm mt-2"
                    >
                        <i className="fas fa-refresh me-1"></i>
                        Refresh
                    </button>
                </div>
            ) : (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-light">
                        <h5 className="card-title mb-0 fw-semibold">
                            <i className="fas fa-users me-2"></i>
                            Pending Admin Requests ({requests.length})
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col" className="ps-4">User</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Requested At</th>
                                        <th scope="col" className="text-center">Status</th>
                                        <th scope="col" className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((request) => (
                                        <tr key={request._id}>
                                            <td className="ps-4">
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0">
                                                        {request.photoURL ? (
                                                            <img
                                                                src={request.photoURL}
                                                                alt={request.name}
                                                                className="rounded-circle"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                {request.name?.charAt(0).toUpperCase() || 'U'}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1 ms-3">
                                                        <div className="fw-medium">{request.name}</div>
                                                        <small className="text-muted">
                                                            {request.registrationType === 'admin_request' ? 'Admin Request' : 'User'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-break">{request.email}</span>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {formatDate(request.createdAt)}
                                                </small>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-warning">
                                                    <i className="fas fa-clock me-1"></i>
                                                    Pending
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="btn-group btn-group-sm" role="group">
                                                    <button
                                                        onClick={() => handleApprove(request._id, request.name)}
                                                        className="btn btn-success"
                                                        title="Approve Admin Request"
                                                        disabled={processing === request._id}
                                                    >
                                                        {processing === request._id ? (
                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-check me-1"></i>
                                                                Approve
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancel(request._id, request.name)}
                                                        className="btn btn-outline-danger"
                                                        title="Cancel Admin Request"
                                                        disabled={processing === request._id}
                                                    >
                                                        {processing === request._id ? (
                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-times me-1"></i>
                                                                Cancel
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRequests;